import Input from "../../../components/custom/forms/Input";
import CheckBox from "../../../components/custom/forms/CheckBox";
import { useContext, useEffect, useMemo, useState, useImperativeHandle, forwardRef } from "react";
import { contextProvider } from "../../../../context/ContextProvider";
import Alerts from "../../../components/Alerts/Alerts";
import { Validate } from "../../../../utils/EventoUtils";
import ReactQuill from "react-quill";
import "./quill.snow.css";
import RestPrivateAntesala from "../../../../helpers/RestPrivateApi";

const Que = forwardRef(({ setForm, form, setValidation, validation, categorias, materialPendiente, setMaterialPendiente }, ref) => {
  const [materialDifusion, setMaterialDifusion] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const context = useContext(contextProvider);
  const { FORMs, VALIDATIONs, URLs } = context;
  const { VALIDATION_FORM_EVENTO } = VALIDATIONs;

  // Cargar material de difusión al montar el componente
  useEffect(() => {
    if (form.id && form.id[0]) {
      cargarMaterialDifusion();
    }
  }, [form.id]);

  const cargarMaterialDifusion = async () => {
    try {
      const response = await RestPrivateAntesala({
        url: `/evento-imagenes?evento_id=${form.id[0]}`,
        method: "get",
        setLoading: () => { }
      });


      if (response.status === 200 && response.data.data) {
        const materialesFiltrados = response.data.data.filter(
          (item) => item.descripcion === "material de difusion"
        );
        setMaterialDifusion(materialesFiltrados);
      }
    } catch (error) {
      console.error("Error al cargar material de difusión:", error);
    }
  };

  // Exponer función para subir material pendiente
  useImperativeHandle(ref, () => ({
    subirMaterialPendiente: async (eventoId, files) => {
      if (!files || files.length === 0) {
        return; // Material de difusión opcional
      }

      if (files.length > 2) {
        throw new Error("No puedes subir más de 2 imágenes de difusión");
      }

      setUploadingMaterial(true);

      for (const file of files) {
        const formData = new FormData();
        formData.append("evento_id", eventoId);
        formData.append("imagen", file);
        formData.append("descripcion", "material de difusion");

        try {
          await RestPrivateAntesala({
            url: "/evento-imagenes",
            formData: formData,
            method: "post",
            setLoading: () => { },
          });
        } catch (error) {
          console.error("Error al subir material pendiente:", error);
        }
      }

      setUploadingMaterial(false);
    },
    validarMaterialDifusion: () => {
      const total = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
      if (total > 2) {
        return { valid: false, message: "No puedes subir más de 2 imágenes de difusión" };
      }
      return { valid: true, message: "" };
    }
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const subirImagenesMaterial = async (files) => {
    // Validar límite de 2 archivos
    const totalActual = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
    const totalNuevo = totalActual + files.length;

    if (totalNuevo > 2) {
      return Alerts({
        msg: "Warning",
        title: `Solo puedes subir un máximo de 2 archivos. Actualmente tienes ${totalActual}.`,
        code: "400",
        type: "error",
      });
    }

    // Si el evento no existe, guardar archivos como pendientes
    if (!form.id || !form.id[0]) {
      setMaterialPendiente((prev) => [...prev, ...files]);
      Alerts({
        msg: "Info",
        title: `${files.length}`,
        code: "200",
        type: "success",
      });
      return;
    }

    // Si el evento ya existe, subir directamente
    setUploadingMaterial(true);

    for (const file of files) {
      const formData = new FormData();
      formData.append("evento_id", form.id[0]);
      formData.append("archivo", file);
      formData.append("descripcion", "material de difusion");

      try {
        const response = await RestPrivateAntesala({
          url: "/evento-imagenes",
          formData: formData,
          method: "post",
          setLoading: setUploadingMaterial,
        });

        if (response.status === 200 || response.status === 201) {
          Alerts({
            msg: "Success",
            title: "Imagen subida correctamente",
            code: "200",
            type: "success",
          });
        } else {
          Alerts({
            msg: "Error",
            title: response.data.msg || "Error al subir la imagen",
            code: "400",
            type: "error",
          });
        }
      } catch (error) {
        Alerts({
          msg: "Error",
          title: "Error al subir la imagen",
          code: "400",
          type: "error",
        });
      }
    }

    await cargarMaterialDifusion();
  };

  const eliminarMaterialDifusion = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta imagen?")) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("_method", "DELETE");

      const response = await RestPrivateAntesala({
        url: `/evento-imagen/${id}`,
        formData: formData,
        method: "post",
      });

      if (response.status === 200) {
        Alerts({
          msg: "Success",
          title: "Imagen eliminada correctamente",
          code: "200",
          type: "success",
        });
        await cargarMaterialDifusion();
      } else {
        Alerts({
          msg: "Error",
          title: response.data.msg || "Error al eliminar la imagen",
          code: "400",
          type: "error",
        });
      }
    } catch (error) {
      Alerts({
        msg: "Error",
        title: "Error al eliminar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const descargarImagen = async (url, nombre) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (error) {
      console.error('Error al descargar la imagen:', error);
      Alerts({
        msg: "Error",
        title: "Error al descargar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const onClick = (e, value) => {
    switch (e.target.name) {
      case "ficha_tecnica-delete":
        {
          let input = form;
          let result = input.ficha_tecnica[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            ficha_tecnica: [result, "required"],
          });
        }
        break;
      case "ficha_tecnica":
        {
          const input_validation = Validate(
            form,
            JSON.parse(JSON.stringify(VALIDATION_FORM_EVENTO)),
            "ficha_tecnica"
          );
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["ficha_tecnica-nombre"][0],
            rol: form["ficha_tecnica-rol"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "link":
        {
          const input_validation = Validate(form, validation, "link");
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["link-nombre"][0],
            url: form["link-url"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "link-delete":
        {
          let input = form;
          let result = input.link[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            link: [result, "required"],
          });
        }
        break;
    }
  };

  const onChange = (e, value) => {
    switch (e.target.name) {
      case "atp":
      case "adultos":
      case "infancias": {
        let input = form;

        if (input[e.target.name][0] == 'SI') {
          input[e.target.name][0] = 'NO';
        } else {
          input[e.target.name][0] = 'SI';
        }

        setForm({
          ...form,
          [e.target.name]: input[e.target.name],
        });
      } break;
      case "duracion":
      case "nombre_grupo":
      case "clase":
      case "grupo":
      case "nombre":
      case "link-nombre":
      case "link-url":
      case "ficha_tecnica-nombre":
      case "ficha_tecnica-rol":
        {
          let input = form;
          input[e.target.name][0] = e.target.value;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "imagen":
        {
          let input = form;
          input[e.target.name][0] = e.target.files[0];
          input[e.target.name + "_url"][0] = URL.createObjectURL(
            e.target.files[0]
          );

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            [e.target.name + "_url"]: input[e.target.name + "_url"],
          });
        }
        break;
      case "categorias":
        {
          let input = form;
          let result = form.categorias[0];
          if (
            result.indexOf(categorias[e.target.id].id) < 0 &&
            result.length < 3
          ) {
            result.push(categorias[e.target.id].id);
          } else {
            delete result[result.indexOf(categorias[e.target.id].id)];
            result = result.filter((i) => i != null);
          }

          input["categorias"][0] = result;

          setForm({
            ...form,
            categorias: input["categorias"],
          });
        }
        break;
    }
  };


  const handleChange = (value) => {
    // Validar que no contenga emojis
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{FE00}-\u{FE0F}\u{1F004}\u{1F0CF}\u{1F18E}\u{3030}\u{2B50}\u{2B55}\u{231A}-\u{231B}\u{23E9}-\u{23EC}\u{23F0}\u{23F3}\u{25FD}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2705}\u{270A}-\u{270B}\u{2728}\u{274C}\u{274E}\u{2753}-\u{2755}\u{2757}\u{2795}-\u{2797}\u{27B0}\u{27BF}\u{2B1B}-\u{2B1C}]/gu;

    if (emojiRegex.test(value)) {
      Alerts({
        msg: "Warning",
        title: "No se permiten emojis en la descripción del evento",
        code: "400",
        type: "error",
      });
      return;
    }

    let input = form;
    input['descripcion'][0] = value;
    setForm({
      ...form,
      descripcion: input['descripcion'],
    });
  }

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        video: function () {
          const url = prompt('Ingresa la URL del video de YouTube:');
          if (url) {
            let embedUrl = url;

            // Convertir URL de YouTube a formato embed
            if (url.includes('youtube.com/watch?v=')) {
              const videoId = url.split('v=')[1].split('&')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            } else if (url.includes('youtu.be/')) {
              const videoId = url.split('youtu.be/')[1].split('?')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }

            const range = this.quill.getSelection(true);
            this.quill.insertEmbed(range.index, 'video', embedUrl);
            this.quill.setSelection(range.index + 1);
          }
        }
      }
    }
  }), []);


  return (
    <div className="container-md  w-100">
      <div
        className="card  p-3 mb-4"
        style={{ borderRadius: "1rem" }}
      >
        <div className="row">
          <div className="col-md-12 mb-3 px-1">
            <h5 className="GothamBold">Información del evento</h5>

            <hr />

            <div className="d-flex">
              <div
                className="overflow-hidden mb-2 me-md-2"
                style={{
                  height: "250px",
                  width: "300px",
                  borderRadius: "10px",
                }}
              >
                <img
                  src={
                    form.imagen_url[0]
                      ? form.imagen_url[0]
                      : "/assets/images/organizaciones.jpg"
                  }
                  alt=""
                  className="w-100 h-100"
                  style={{ objectFit: "cover" }}
                />
              </div>
              <div>
                <label
                  className="form-label text-muted"
                  style={{ fontSize: "14px" }}
                >
                  Imagen principal
                  <span className="fw-bold ms-2">
                    Tamaño de imagen 1:1 1080 x 1080 px
                  </span>
                </label>
                <Input
                  onChange={onChange}
                  name={"imagen"}
                  type={"file"}
                  svg={
                    ' <svg width="10" height="10" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_3124_63597)"><path d="M8.08541 14.5625C9.96177 14.5625 11.7613 13.8711 13.0881 12.6404C14.4148 11.4097 15.1602 9.74048 15.1602 8C15.1602 6.25952 14.4148 4.59032 13.0881 3.35961C11.7613 2.1289 9.96177 1.4375 8.08541 1.4375C6.20906 1.4375 4.40956 2.1289 3.08277 3.35961C1.75599 4.59032 1.01061 6.25952 1.01061 8C1.01061 9.74048 1.75599 11.4097 3.08277 12.6404C4.40956 13.8711 6.20906 14.5625 8.08541 14.5625ZM8.08541 15.5C5.94101 15.5 3.88443 14.7098 2.36811 13.3033C0.851788 11.8968 -7.24792e-05 9.98912 -7.24792e-05 8C-7.24792e-05 6.01088 0.851788 4.10322 2.36811 2.6967C3.88443 1.29018 5.94101 0.5 8.08541 0.5C10.2298 0.5 12.2864 1.29018 13.8027 2.6967C15.319 4.10322 16.1709 6.01088 16.1709 8C16.1709 9.98912 15.319 11.8968 13.8027 13.3033C12.2864 14.7098 10.2298 15.5 8.08541 15.5V15.5Z" fill="white" /><path d="M7.14564 6.67625L9.46012 6.94531L9.54299 7.30156L9.08818 7.37938C8.79104 7.445 8.73242 7.54438 8.79711 7.81906L9.54299 11.0703C9.73906 11.9113 9.43687 12.3069 8.72636 12.3069C8.17553 12.3069 7.53577 12.0706 7.2457 11.7463L7.15676 11.3563C7.3589 11.5213 7.65402 11.5869 7.85009 11.5869C8.12803 11.5869 8.2291 11.4059 8.15734 11.0872L7.14564 6.67625ZM7.0749 4.71875C7.0749 4.96739 7.18138 5.20585 7.37092 5.38166C7.56046 5.55748 7.81753 5.65625 8.08558 5.65625C8.35363 5.65625 8.6107 5.55748 8.80025 5.38166C8.98979 5.20585 9.09627 4.96739 9.09627 4.71875C9.09627 4.47011 8.98979 4.23165 8.80025 4.05584C8.6107 3.88002 8.35363 3.78125 8.08558 3.78125C7.81753 3.78125 7.56046 3.88002 7.37092 4.05584C7.18138 4.23165 7.0749 4.47011 7.0749 4.71875Z" fill="white" /> </g><defs><clipPath id="clip0_3124_63597"> <rect width="16.171" height="15" fill="white" transform="matrix(-1 0 0 1 16.1709 0.5)" /> </clipPath> </defs></svg>'
                  }
                  validate={validation.imagen_url}
                  disabled={false}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 col-12 mb-3">
              <Input
                onChange={onChange}
                label={"Nombre del evento"}
                name={"nombre"}
                type={"text"}
                value={form.nombre[0]}
                svg={""}
                validate={validation.nombre}
                disabled={false}
              />

              <Input
                onChange={onChange}
                label={"Nombre del grupo (opcional)"}
                name={"nombre_grupo"}
                type={"text"}
                value={form.nombre_grupo[0]}
                svg={""}
                validate={validation.nombre_grupo}
                disabled={false}
              />

              <Input
                onChange={onChange}
                label={"Duracion del evento en minutos"}
                name={"duracion"}
                type={"number"}
                value={form.duracion[0]}
                svg={""}
                validate={validation.duracion}
                disabled={false}
              />
            </div>

            <div className="col-md-4 col-12 mb-3">
              <span className="text-muted">
                Selecciona tu categoria (Hasta 3 categorias)
              </span>
              {categorias
                ? categorias.map((c, index) => (
                  <CheckBox
                    onChange={onChange}
                    label={c.nombre}
                    name={"categorias"}
                    type={"checkbox"}
                    checked={
                      form.categorias[0].indexOf(c.id) < 0 ? false : true
                    }
                    uniqueKey={index}
                    disabled={false}
                  />
                ))
                : ""}

              {validation.categorias.status ? (
                <div className="text-danger">{validation.categorias.msg}</div>
              ) : (
                ""
              )}
            </div>

            <div className="col-md-4 col-12 mb-3">
              Tipo de publico
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  ATP
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="atp"
                    id="flexRadioDefault1"
                    value={"ATP"}
                    checked={form.atp[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  ADULTOS
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="adultos"
                    id="flexRadioDefault1"
                    value={"ADULTOS"}
                    checked={form.adultos[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  INFANCIAS
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="infancias"
                    id="flexRadioDefault1"
                    value={"INFANCIAS"}
                    checked={form.infancias[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              {validation.grupo.status ? (
                <div className="text-danger">{validation.grupo.msg}</div>
              ) : (
                ""
              )}
            </div>

            <div className="col-md-4 col-12 mb-3 px-md-5 text-nowrap d-none">
              Tipo de evento
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  ONLINE
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"ONLINE"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "ONLINE" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  PRESENCIAL
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"PRESENCIAL"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "PRESENCIAL" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              {validation.clase.status ? (
                <div className="text-danger">{validation.clase.msg}</div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="card p-3 mb-4"
        style={{ borderRadius: "1rem" }}
      >
        <div className="row">
          <div className="col-md-12 mb-3">
            <h5>Describe tu evento</h5>
            <hr />
            <div>
              <ReactQuill
                value={form?.descripcion[0]}
                onChange={handleChange}
                theme="snow"
                modules={modules}
              />
            </div>
          </div>
          {validation.descripcion.status ? (
            <div className="text-danger">{validation.descripcion.msg}</div>
          ) : (
            ""
          )}
        </div>
      </div>

      <div
        className="card  p-3 mb-4"
        style={{ borderRadius: "1rem" }}
      >
        <div className="row">
          <div className="col-md-6 col-12 mb-3">
            <h5>Ficha técnica</h5>
            <span className="text-muted">
              Ingresa el rol que tendran los participantes.
            </span>
            <hr />
            <Input
              onChange={onChange}
              label={"Nombre de participante"}
              name={"ficha_tecnica-nombre"}
              type={"text"}
              value={form["ficha_tecnica-nombre"][0]}
              icon={'<i class="bi bi-arrow-right"></i>'}
              validate={validation["ficha_tecnica-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"Rol de participante"}
              name={"ficha_tecnica-rol"}
              type={"text"}
              value={form["ficha_tecnica-rol"][0]}
              icon={'<i class="bi bi-arrow-right"></i>'}
              validate={validation["ficha_tecnica-rol"]}
              disabled={false}
            />
            <label className="btn btn-custom-naranja fw-normal GothamBold text-white w-100 mt-1">
              Añadir participante
              <button
                className="d-none"
                name="ficha_tecnica"
                onClick={(event) => onClick(event)}
              ></button>
            </label>
            {validation.ficha_tecnica.status ? (
              <div className="text-danger">{validation.ficha_tecnica.msg}</div>
            ) : (
              ""
            )}
          </div>
          <div className="col-md-6">
            <ul class="list-group">
              {form.ficha_tecnica[0].map((f, index) => (
                <li class="list-group-item border-0 text-muted d-flex justify-content-between shadow-sm mb-3">
                  <div>
                    <span className="text-black fw-bold">{f.rol}: </span>
                    {f.nombre}
                  </div>
                  <label
                    className="btn btn-outline-danger"
                    style={{ maxHeight: "40px" }}
                  >
                    <i class="bi bi-trash"></i>
                    <button
                      className="d-none"
                      name="ficha_tecnica-delete"
                      onClick={(event) => onClick(event, index)}
                    ></button>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="card  p-3 mb-4" style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <h5>Links para mi evento (Opcional)</h5>
            <span className="text-muted">Ingresa la URL para el evento.</span>
            <hr />
            <Input
              onChange={onChange}
              label={"Nombre"}
              name={"link-nombre"}
              type={"text"}
              value={form["link-nombre"][0]}
              icon={'<i class="bi bi-arrow-right"></i>'}
              validate={validation["link-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"URL"}
              name={"link-url"}
              type={"text"}
              value={form["link-url"][0]}
              icon={'<i class="bi bi-arrow-right"></i>'}
              validate={validation["link-url"]}
              disabled={false}
            />
            <label className="btn btn-custom-naranja fw-normal GothamBold text-white w-100 mt-1">
              Añadir link
              <button
                className="d-none"
                name="link"
                onClick={(event) => onClick(event)}
              ></button>
            </label>
          </div>

          <div className="col-md-6">
            <ul class="list-group">
              {form.link[0].map((l, index) => (
                <li class="list-group-item border-0 text-muted d-flex justify-content-between shadow-sm mb-3">
                  <div>
                    <span className="text-black fw-bold">{l.nombre}: </span>
                    {l.url}
                  </div>
                  <label
                    className="btn btn-outline-danger"
                    style={{ maxHeight: "40px" }}
                  >
                    <i class="bi bi-trash"></i>
                    <button
                      className="d-none"
                      name="link-delete"
                      onClick={(event) => onClick(event, index)}
                    ></button>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="card p-3 mb-4" style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <h5>Material de Difusión (Opcional)</h5>
            <span className="text-muted">
              Puedes subir hasta 2 imágenes o fotografías del espectáculo sin logo ni texto
              {!form.id[0]}
            </span>
            <hr />

            {/* Zona de drag and drop */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border rounded p-5 text-center ${isDragging ? "border-primary bg-light" : "border-secondary"
                }`}
              style={{
                borderStyle: "dashed",
                borderWidth: "2px",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
            >
              <input
                type="file"
                id="material-difusion-input"
                multiple
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                disabled={
                  (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                }
              />
              <label
                htmlFor="material-difusion-input"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <div>
                  <i
                    className="bi bi-cloud-upload"
                    style={{ fontSize: "3rem", color: "#6c757d" }}
                  ></i>
                  <p className="mt-3 mb-0 text-muted">
                    {uploadingMaterial
                      ? "Subiendo imágenes..."
                      : isDragging
                        ? "Suelta las imágenes aquí"
                        : "Arrastra imágenes aquí o haz clic para seleccionar"}
                  </p>
                  <small className="text-muted">
                    Formatos: JPG, PNG, GIF. Puedes subir múltiples archivos.
                  </small>
                </div>
              </label>
            </div>

            {/* Contador de archivos */}
            <div className="mt-3">
              <small className={`badge ${
                (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) === 0
                  ? 'bg-danger'
                  : (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                    ? 'bg-success'
                    : 'bg-warning'
              }`}>
                {form.id && form.id[0] ? materialDifusion.length : materialPendiente.length} / 2 imágenes
              </small>
            </div>

            {/* Vista previa de archivos pendientes (para eventos nuevos) */}
            {!form.id[0] && materialPendiente.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes pendientes ({materialPendiente.length}/2):</h6>
                <div className="row">
                  {materialPendiente.map((file, index) => (
                    <div key={index} className="col-md-3 col-sm-6 mb-3">
                      <div
                        className="card shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt="Material pendiente"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => {
                            const url = URL.createObjectURL(file);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = file.name;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() =>
                            setMaterialPendiente((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        <div
                          className="badge bg-warning text-dark"
                          style={{
                            position: "absolute",
                            bottom: "5px",
                            left: "5px",
                          }}
                        >
                          Pendiente
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Galería de imágenes subidas */}
            {form.id[0] && materialDifusion.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes cargadas ({materialDifusion.length}/2):</h6>
                <div className="row">
                  {materialDifusion.map((material) => (
                    <div
                      key={material.id}
                      className="col-md-3 col-sm-6 mb-3"
                    >
                      <div
                        className="card shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={material.url}
                          alt="Material de difusión"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => descargarImagen(material.url, `material-difusion-${material.id}.jpg`)}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() => eliminarMaterialDifusion(material.id)}
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Que;
